/**
 * icons — the redesign's own quick-action marks.
 *
 * GOOGIE-5. Rennan sent a picture of the Today action rail with six
 * atomic-age icons in the badges and said: these. Lucide's Plus /
 * Sparkles / Camera / Receipt / Telescope / Calendar are a fine
 * modern set and completely wrong for this design — a 1958 dashboard
 * doesn't get 2019 hairline glyphs.
 *
 * So these are drawn rather than imported: a rocket, an atom, a
 * console television, an atomic sun, a robot and a wind-up clock,
 * all on a 24-box, all painting in `currentColor` so a badge sets
 * the colour and the mark follows. They carry their own stroke
 * weight (2.1) because the redesign's line is heavier than the
 * app's, and they're marked `data-keep-stroke` so the global
 * stroke-width rule in googie.css leaves them alone.
 *
 * Every one is original. They're built from the shapes the era
 * actually used — circles, ellipses, radiating spokes — not traced
 * from anyone's icon set.
 */
import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement>;

function Svg({ children, ...rest }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.1}
      strokeLinecap="round"
      strokeLinejoin="round"
      data-keep-stroke
      aria-hidden
      {...rest}
    >
      {children}
    </svg>
  );
}

/** New project — a rocket on the pad, fins out, one porthole. */
export function GRocket(props: IconProps) {
  return (
    <Svg {...props}>
      {/* body */}
      <path d="M12 2.6c2.6 2.2 4 5.3 4 8.7v3.6H8v-3.6c0-3.4 1.4-6.5 4-8.7z" />
      {/* porthole */}
      <circle cx="12" cy="9.4" r="1.7" />
      {/* fins */}
      <path d="M8 12.2 5.3 14.6v3.1L8 15.9M16 12.2l2.7 2.4v3.1L16 15.9" />
      {/* exhaust */}
      <path d="M10.4 18.2c.5 1.4 1 2.4 1.6 3.2.6-.8 1.1-1.8 1.6-3.2" />
    </Svg>
  );
}

/** Add lead — an atom: a nucleus and three orbits. */
export function GAtom(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="1.9" fill="currentColor" stroke="none" />
      <ellipse cx="12" cy="12" rx="9.2" ry="3.7" />
      <ellipse cx="12" cy="12" rx="9.2" ry="3.7" transform="rotate(60 12 12)" />
      <ellipse cx="12" cy="12" rx="9.2" ry="3.7" transform="rotate(120 12 12)" />
    </Svg>
  );
}

/** New gallery — a console television: rounded screen, rabbit-ear
 *  antenna, two tuning knobs down the side. */
export function GTelevision(props: IconProps) {
  return (
    <Svg {...props}>
      {/* antenna */}
      <path d="M8.6 5.1 11 8.2M15.4 5.1 13 8.2" />
      <circle cx="8" cy="4.3" r="1.05" fill="currentColor" stroke="none" />
      <circle cx="16" cy="4.3" r="1.05" fill="currentColor" stroke="none" />
      {/* cabinet */}
      <rect x="2.7" y="8.2" width="18.6" height="11.4" rx="3.4" />
      {/* screen */}
      <rect x="5.1" y="10.5" width="10.4" height="6.8" rx="2.2" />
      {/* knobs */}
      <circle cx="18.4" cy="12.4" r="1.05" fill="currentColor" stroke="none" />
      <circle cx="18.4" cy="15.6" r="1.05" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** Log expense — the atomic sun: a disc with radiating spokes. */
export function GSunburst(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="4.2" />
      <path d="M12 1.9v2.9M12 19.2v2.9M22.1 12h-2.9M4.8 12H1.9" />
      <path d="m19.1 4.9-2.05 2.05M6.95 17.05 4.9 19.1M19.1 19.1l-2.05-2.05M6.95 6.95 4.9 4.9" />
    </Svg>
  );
}

/** Ask Nova — a robot: antenna, head, two eyes, a jaw. Nova's own
 *  character is untouchable and lives on /assistant; this is a mark
 *  for a button, drawn from the same era as everything around it. */
export function GRobot(props: IconProps) {
  return (
    <Svg {...props}>
      {/* antenna */}
      <path d="M12 2.4v2.6" />
      <circle cx="12" cy="1.9" r="1.15" fill="currentColor" stroke="none" />
      {/* head */}
      <rect x="3.4" y="5" width="17.2" height="11.3" rx="4" />
      {/* eyes */}
      <circle cx="8.9" cy="10.6" r="1.35" fill="currentColor" stroke="none" />
      <circle cx="15.1" cy="10.6" r="1.35" fill="currentColor" stroke="none" />
      {/* ears */}
      <path d="M3.4 9.4H1.9v3.4h1.5M20.6 9.4h1.5v3.4h-1.5" />
      {/* jaw */}
      <path d="M8.6 18.9h6.8" />
      <path d="M9.6 16.3v2.6M14.4 16.3v2.6" />
    </Svg>
  );
}

/** Calendar — a wind-up alarm clock: bells, legs, hands at ten-past. */
export function GAlarmClock(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="13" r="7.6" />
      {/* hands */}
      <path d="M12 8.9V13l2.8 1.7" />
      {/* bells */}
      <path d="M5.2 6.1a4 4 0 0 1 4-2.5M18.8 6.1a4 4 0 0 0-4-2.5" />
      {/* legs */}
      <path d="m6.9 19.4-1.4 1.9M17.1 19.4l1.4 1.9" />
    </Svg>
  );
}

/**
 * Invoices — a bill with a coin on it. INVOICE-1.
 *
 * The seventh mark, drawn rather than borrowed: the quick-action rail
 * works because every pill carries a Googie mark in Orbit blue, and
 * one lucide glyph among six of these would read as a different
 * control that happened to land in the row.
 *
 * A torn-off receipt rather than a dollar sign — the rail is read at
 * 22px, where a glyph that is mostly one stroke reads as a squiggle,
 * and because a currency symbol is wrong the moment a studio invoices
 * in anything but dollars.
 */
export function GReceipt(props: IconProps) {
  return (
    <Svg {...props}>
      {/* the slip, torn along the bottom */}
      <path d="M6.2 3.4h11.6v15.1l-1.9-1.3-1.9 1.3-1.9-1.3-1.9 1.3-1.9-1.3-2.1 1.3z" />
      {/* two lines of it */}
      <path d="M9.1 7.6h5.8M9.1 11h3.6" />
    </Svg>
  );
}

export const GOOGIE_ICONS = {
  rocket: GRocket,
  atom: GAtom,
  television: GTelevision,
  sunburst: GSunburst,
  robot: GRobot,
  clock: GAlarmClock,
  receipt: GReceipt,
};
